import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import React, { useEffect, useRef, useState, useContext, useMemo } from 'react';
import { io } from "socket.io-client";
import "./ChatWithStaff.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import EditIcon from "@mui/icons-material/Edit";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import Person4Icon from "@mui/icons-material/Person4";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIosNew";
import { ProductContext } from "../context/AppContext";
import { Button, Form, InputGroup, Alert, Spinner, Dropdown } from "react-bootstrap";
import Footer from './Common_Components/Footer';
import { 
  FaPaperclip, FaFileUpload, FaTimes, FaComments, 
  FaUserFriends, FaUser, FaUniversity, FaCaretDown 
} from "react-icons/fa";
import { format } from "date-fns";

// Import apiService if available, otherwise create a simple fetch wrapper
const apiService = {
  get: async (url, params = {}) => {
    const queryString = Object.keys(params).length > 0 
      ? '?' + new URLSearchParams(params).toString() 
      : '';
    const response = await fetch(`${url}${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("AuthToken")}`,
      },
    });
    return response.json();
  },
  post: async (url, data, options = {}) => {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("AuthToken")}`,
        ...options.headers,
      },
      body: data,
    });
    return response.json();
  }
};

const ChatWithStaff = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username, Translate } = useContext(ProductContext);
  const [activePage, setActivePage] = useState("Profile");
  const authToken = localStorage.getItem("AuthToken");
  
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const senderId = username?.message?.userDetails?.user_id || storedUser?.user_id || "";
  const defaultCollegeId = username?.message?.userDetails?.college_id || storedUser?.college_id || 0;
  const notificationParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      chatType: params.get("chatType") || "",
      staffTarget: params.get("staffTarget") || "",
      participantId: params.get("participantId") || "",
      chatId: params.get("chatId") || "",
    };
  }, [location.search]);

  // State variables
  const [socketStatus, setSocketStatus] = useState("disconnected");
  const [chatMode, setChatMode] = useState(notificationParams.chatType === "individual" ? "individual" : "group");
  const [staffTarget, setStaffTarget] = useState("erish"); // erish or college
  const [selectedStaff, setSelectedStaff] = useState(null); // Selected staff member for individual chat
  const [staffList, setStaffList] = useState([]); // List of staff members
  const [staffLoading, setStaffLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [selectedStaffIdFromNotification, setSelectedStaffIdFromNotification] = useState(notificationParams.participantId);

  // File upload states - AdminChat.js style
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFilePreview, setSelectedFilePreview] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const fileInputRef = useRef(null);

  const socketRef = useRef(null);
  const scrollRef = useRef(null);
  const receiverIdRef = useRef(null);
  const chatModeRef = useRef(chatMode);
  const selectedStaffRef = useRef(selectedStaff);
  const selectedFilePreviewRef = useRef(selectedFilePreview);

  const socketUrl =
    process.env.REACT_APP_SOCKET_URL ||
    process.env.REACT_APP_BASE_URL ||
    "https://api.japaking.online";
  const chatApiBaseUrl =
    process.env.REACT_APP_CHAT_API_BASE_URL ||
    process.env.REACT_APP_BASE_URL ||
    "https://api.japaking.online";

  useEffect(() => {
    chatModeRef.current = chatMode;
  }, [chatMode]);

  useEffect(() => {
    selectedStaffRef.current = selectedStaff;
  }, [selectedStaff]);

  useEffect(() => {
    selectedFilePreviewRef.current = selectedFilePreview;
  }, [selectedFilePreview]);

  useEffect(() => {
    if (notificationParams.chatType === "group" || notificationParams.chatType === "individual") {
      setChatMode(notificationParams.chatType);
    }

    if (notificationParams.staffTarget === "college" || notificationParams.staffTarget === "erish") {
      setStaffTarget(notificationParams.staffTarget);
    }

    if (notificationParams.participantId) {
      setSelectedStaffIdFromNotification(notificationParams.participantId);
    }
  }, [notificationParams.chatType, notificationParams.staffTarget, notificationParams.participantId]);

  // Update receiverIdRef when selectedStaff changes
  useEffect(() => {
    receiverIdRef.current = selectedStaff ? selectedStaff.user_id.toString() : "";
  }, [selectedStaff]);

  // ========== SOCKET CONNECTION ==========
  useEffect(() => {
    if (!senderId) {
      setErrorMessage("Missing user id. Please login again.");
      return;
    }

    const socket = io(socketUrl, {
      transports: ["websocket"],
      withCredentials: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketStatus("connected");
      socket.emit("join", { user_id: senderId });
    });

    socket.on("disconnect", () => setSocketStatus("disconnected"));
    socket.on("connect_error", (error) => {
      console.error("Chat socket connect_error:", error);
      setSocketStatus("disconnected");
    });

    // Handle incoming messages - AdminChat.js style
    const currentUserId = Number(senderId);
    const appendIfRelevant = (chat) => {
      if (chatModeRef.current === "group") {
        const chatSenderId = Number(chat?.sender_id || chat?.sender_user?.user_id || 0);
        const chatReceiverId = Number(chat?.receiver_id || chat?.receiver_user?.user_id || 0);
        const isCurrentUserChat =
          chatSenderId === currentUserId || chatReceiverId === currentUserId;

        if (chat.chat_type === "group" && isCurrentUserChat) {
          setMessages((prev) => {
            if (prev.some((m) => m.chat_id === chat.chat_id)) return prev;
            return [...prev, chat];
          });
        }
        return;
      }

      const currentSelectedStaff = selectedStaffRef.current;
      if (!currentSelectedStaff) return;

      const senderValue = Number(senderId);
      const receiverValue = Number(currentSelectedStaff.user_id);

      const isPair =
        (chat.sender_id === senderValue && chat.receiver_id === receiverValue) ||
        (chat.sender_id === receiverValue && chat.receiver_id === senderValue);

      if (isPair) {
        setMessages((prev) => {
          if (prev.some((m) => m.chat_id === chat.chat_id)) return prev;
          return [...prev, chat];
        });
      }
    };

    socket.on("chat:new", appendIfRelevant);
    socket.on("chat:sent", appendIfRelevant);

    socket.on("notification:new", (notification) => {
      // Handle notifications if needed
    });

    return () => {
      if (selectedFilePreviewRef.current) URL.revokeObjectURL(selectedFilePreviewRef.current);
      socket.disconnect();
    };
  }, [senderId, socketUrl]);

  // Scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load staff members when mode or target changes
  useEffect(() => {
    fetchStaffMembers();
    if (chatMode !== "individual") {
      setSelectedStaff(null);
    }
  }, [chatMode, staffTarget, defaultCollegeId]);

  useEffect(() => {
    if (chatMode !== "individual" || !selectedStaffIdFromNotification || !staffList.length) return;

    const matchedStaff = staffList.find(
      (staff) => String(staff.user_id) === String(selectedStaffIdFromNotification)
    );

    if (matchedStaff) {
      setSelectedStaff((prev) =>
        prev?.user_id === matchedStaff.user_id ? prev : matchedStaff
      );
    }
  }, [chatMode, selectedStaffIdFromNotification, staffList]);

  // ========== FETCH CHAT HISTORY ==========
  useEffect(() => {
    if (!senderId) return;

    if (chatMode === "individual" && !selectedStaff) {
      setMessages([]);
      return;
    }

    fetchHistory(true);
  }, [chatMode, selectedStaff, senderId, authToken]);

  const fetchHistory = async (showLoading = true) => {
    if (!senderId) return;

    // For group chat
    if (chatMode === "group") {
      try {
        if (showLoading) setHistoryLoading(true);
        const response = await apiService.get(`${chatApiBaseUrl}/api/chat-rt/history`, {
          studentId: Number(senderId),
          chatType: "group",
        });
        
        if (response?.success && Array.isArray(response?.message?.history)) {
          setMessages(response.message.history);
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error("Error fetching group chat history:", error);
        setMessages([]);
      } finally {
        if (showLoading) setHistoryLoading(false);
      }
      return;
    }

    // For individual chat
    if (chatMode === "individual" && selectedStaff) {
      try {
        if (showLoading) setHistoryLoading(true);
        const response = await apiService.get(`${chatApiBaseUrl}/api/chat-rt/history`, {
          studentId: Number(senderId),
          chatType: "individual",
          staffId: Number(selectedStaff.user_id),
        });
        
        if (response?.success && Array.isArray(response?.message?.history)) {
          setMessages(response.message.history);
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error("Error fetching individual chat history:", error);
        setMessages([]);
      } finally {
        if (showLoading) setHistoryLoading(false);
      }
    }
  };

  // ========== FETCH STAFF MEMBERS ==========
  const fetchStaffMembers = async () => {
    try {
      setStaffLoading(true);
      setErrorMessage("");

      if (chatMode === "group") {
        // Fetch both Erish and College staff for name resolution
        const erishRes = await apiService.get(`${chatApiBaseUrl}/api/user-admin/non-students`);
        const erishUsers = (erishRes.success && Array.isArray(erishRes.message?.users)) ? erishRes.message.users : [];

        let collegeUsers = [];
        if (defaultCollegeId) {
           const collegeRes = await apiService.get(`${chatApiBaseUrl}/api/user-admin/non-students`, { collegeId: defaultCollegeId });
           collegeUsers = (collegeRes.success && Array.isArray(collegeRes.message?.users)) ? collegeRes.message.users : [];
        }

        const allUsers = [...erishUsers, ...collegeUsers];
        // Deduplicate
        const uniqueUsers = Array.from(new Map(allUsers.map(u => [u.user_id, u])).values());
        setStaffList(uniqueUsers);
      } else {
        // Individual mode
        const params = {};
        if (staffTarget === "college" && defaultCollegeId) {
          params.collegeId = defaultCollegeId;
        }

        const response = await apiService.get(`${chatApiBaseUrl}/api/user-admin/non-students`, params);

        if (response.success && Array.isArray(response?.message?.users)) {
          const filteredStaff = response.message.users.filter((user) => {
            return staffTarget === "college" ? user.college_id : !user.college_id;
          });
          setStaffList(filteredStaff);
          if (filteredStaff.length === 0) {
            setErrorMessage(`No ${staffTarget === "college" ? "college staff" : "Erish admin"} found.`);
          }
        } else {
          setStaffList([]);
          setErrorMessage("Failed to load staff members.");
        }
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      setStaffList([]);
      if (chatMode === "individual") setErrorMessage("Error loading staff members. Please try again.");
    } finally {
      setStaffLoading(false);
    }
  };

  // ========== FILE HANDLERS (AdminChat.js style) ==========
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Clear previous preview
    if (selectedFilePreview) URL.revokeObjectURL(selectedFilePreview);
    
    setSelectedFile(file);
    setSelectedFilePreview(URL.createObjectURL(file));
    setSelectedFileType(file.type || "");
    setErrorMessage("");
    
    // Clear file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearSelectedFile = () => {
    if (selectedFilePreview) URL.revokeObjectURL(selectedFilePreview);
    setSelectedFile(null);
    setSelectedFilePreview("");
    setSelectedFileType("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // ========== SEND MESSAGE (AdminChat.js style) ==========
  const handleSend = async () => {
    if (isSending) return;

    setErrorMessage("");
    setSuccessMessage("");
    setIsSending(true);

    if (!draft.trim() && !selectedFile) {
      setErrorMessage("Please write a message or attach a file.");
      setIsSending(false);
      return;
    }

    // Determine receiver
    let receiverIdToSend = null;
    if (chatMode === "individual") {
      if (!selectedStaff) {
        setErrorMessage("Please select a staff member to chat with.");
        setIsSending(false);
        return;
      }
      receiverIdToSend = selectedStaff.user_id;
    } else if (chatMode === "group") {
      receiverIdToSend = senderId;
    }

    try {
      if (selectedFile) {
        // File upload - AdminChat.js style
        const formData = new FormData();
        formData.append("sender_id", senderId.toString());
        
        if (receiverIdToSend) {
          formData.append("receiver_id", receiverIdToSend.toString());
        }
        
        formData.append("message", draft.trim());
        formData.append("chat_type", chatMode);
        formData.append("staff_target", chatMode === "individual" ? staffTarget : "group");
        
        if (defaultCollegeId) {
          formData.append("college_id", defaultCollegeId.toString());
        }
        
        formData.append("file", selectedFile);

        const response = await apiService.post(`${chatApiBaseUrl}/api/chat-rt/send`, formData, {
          headers: {},
        });

        if (!response?.success) {
          setErrorMessage("Failed to send message with file.");
          setIsSending(false);
          return;
        }

        // File upload successful - refresh history like AdminChat.js
        await fetchHistory(false);
        
      } else {
        // Text-only message via socket
        if (!socketRef.current || socketStatus !== "connected") {
          setErrorMessage("Connection error. Please try again.");
          setIsSending(false);
          return;
        }

        const normalizedCollegeId = String(defaultCollegeId || "").trim();
        const numericCollegeId = Number(normalizedCollegeId);
        const hasCollegeId =
          normalizedCollegeId !== "" &&
          !Number.isNaN(numericCollegeId) &&
          numericCollegeId > 0;

        const messageData = {
          sender_id: Number(senderId),
          receiver_id: receiverIdToSend ? Number(receiverIdToSend) : null,
          message: draft.trim(),
          college_id: hasCollegeId ? Number(numericCollegeId) : null,
          chat_type: chatMode,
          staff_target: chatMode === "individual" ? staffTarget : "group",
          url: null,
          file_name: null
        };

        socketRef.current.emit("chat:send", messageData);
      }

      // Clear form
      setDraft("");
      clearSelectedFile();
      setSuccessMessage("Message sent successfully!");
      
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error('Send error:', error);
      setErrorMessage("Error sending message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSending) {
      e.preventDefault();
      handleSend();
    }
  };

  // ========== RENDER FUNCTIONS ==========
  const renderFilePreview = () => {
    if (!selectedFile) return null;

    if (selectedFileType.startsWith("image/")) {
      return (
        <div className="file-preview mt-2">
          <img 
            src={selectedFilePreview} 
            alt="Preview" 
            style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }}
          />
          <div className="small text-muted mt-1">{selectedFile.name}</div>
        </div>
      );
    } else if (selectedFileType.startsWith("video/")) {
      return (
        <div className="file-preview mt-2">
          <video 
            controls 
            style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px' }}
          >
            <source src={selectedFilePreview} type={selectedFileType} />
          </video>
          <div className="small text-muted mt-1">{selectedFile.name}</div>
        </div>
      );
    } else {
      return (
        <div className="file-preview mt-2 p-2 border rounded bg-light">
          <FaFileUpload className="me-2" />
          <strong>{selectedFile.name}</strong>
          <br />
          <small>{(selectedFile.size / 1024).toFixed(2)} KB</small>
        </div>
      );
    }
  };

  const getChatTitle = () => {
    if (chatMode === "group") {
      return defaultCollegeId 
        ? "College Group Chat" 
        : "Erish Admin Group Chat";
    } else {
      if (selectedStaff) {
        return `Chat with ${selectedStaff.name}`;
      }
      return staffTarget === "college" 
        ? "Chat with College Staff" 
        : "Chat with Erish Admin";
    }
  };

  const getSubtitle = () => {
    if (chatMode === "group") {
      return defaultCollegeId 
        ? "All college staff + Erish admin" 
        : "Erish admin group conversation";
    } else {
      if (selectedStaff) {
        return `${selectedStaff.role?.role_name || 'Staff'} • ${selectedStaff.email}`;
      }
      return "Select a staff member to start chatting";
    }
  };

  const getSenderDesignation = (msg) => {
    const senderRoleName =
      msg?.sender_role?.role_name ||
      msg?.sender_user?.role?.role_name ||
      msg?.role?.role_name ||
      "";
    const normalizedRole = senderRoleName.toLowerCase();

    if (normalizedRole.includes("student")) {
      return "Student";
    }

    if (normalizedRole.includes("admin")) {
      return "Staff";
    }

    return senderRoleName || "Staff";
  };

  const getSenderRoleName = (msg) =>
    msg?.sender_role?.role_name ||
    msg?.sender_user?.role?.role_name ||
    msg?.role?.role_name ||
    "Staff";

  const getSenderLabel = (msg) => {
    if (msg.sender_id === Number(senderId)) return "You";
    return getSenderRoleName(msg);
  };

  const renderMessageTextWithLinks = (text, isOwnMessage) => {
    if (!text) return "";
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split("\n").map((line, lineIdx) => {
      const parts = line.split(urlRegex);
      const renderedLine = parts.map((part, partIdx) => {
        if (part.match(urlRegex)) {
          return (
            <a 
              key={`part-${partIdx}`} 
              href={part} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                color: isOwnMessage ? "#ffffff" : "#0056b3", 
                textDecoration: "underline", 
                wordBreak: "break-all" 
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return part;
      });
      return (
        <React.Fragment key={`line-${lineIdx}`}>
          {renderedLine}
          {lineIdx < text.split("\n").length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  const formatMsgTime = (sentAt) => {
    if (!sentAt) return "Just now";
    try {
      const dateStr = (typeof sentAt === 'string' && !sentAt.endsWith("Z")) ? sentAt + "Z" : sentAt;
      const date = new Date(dateStr);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
    } catch {
      return "Just now";
    }
  };

  // ========== MAIN RENDER ==========
  return (
    <div className="Profile_Design_Main_Div_11">
      <div className="header_inner">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <ArrowBackIosIcon
                onClick={() => navigate(-1)}
                className="icon_1"
                style={{ cursor: 'pointer' }}
              />
              <h2>Chat With Staff</h2>
              <ChatBubbleOutlineIcon onClick={() => navigate('/chat-with-staff')} className="logo_1" style={{ cursor: 'pointer', color: '#ffc0cb' }} />
            </div>
          </div>
        </div>
      </div>

      <div className="chat-with-staff">
        <div className="chat-with-staff__header">
          <div>
            <p>Realtime messages with support team and staff</p>
          </div>
          <div className={`chat-with-staff__status chat-with-staff__status--${socketStatus}`}>
            {socketStatus}
          </div>
        </div>

        <div className="chat-with-staff__body">
          {/* Left Panel: Chat Settings */}
          <section className="chat-with-staff__panel">
            <h5 className="mb-3">Chat Settings</h5>

            {/* Chat Type Selection */}
            <label className="chat-with-staff__label">Chat Type</label>
            <div className="chat-with-staff__options mb-3">
              <label 
                className={`chat-with-staff__option ${chatMode === "group" ? "active" : ""}`}
                style={chatMode === "group" ? { backgroundColor: "#ffc0cb", borderColor: "#ffc0cb", color: "white" } : {}}
              >
                <input
                  type="radio"
                  name="chat-mode"
                  value="group"
                  checked={chatMode === "group"}
                  onChange={() => setChatMode("group")}
                />
                <FaUserFriends className="me-1" />
                <span>Group Chat</span>
              </label>
              <label 
                className={`chat-with-staff__option ${chatMode === "individual" ? "active" : ""}`}
                style={chatMode === "individual" ? { backgroundColor: "#ffc0cb", borderColor: "#ffc0cb", color: "white" } : {}}
              >
                <input
                  type="radio"
                  name="chat-mode"
                  value="individual"
                  checked={chatMode === "individual"}
                  onChange={() => setChatMode("individual")}
                />
                <FaUser className="me-1" />
                <span>Individual Chat</span>
              </label>
            </div>

            {/* Individual Chat Options */}
            {chatMode === "individual" && (
              <>
                <label className="chat-with-staff__label">Chat With</label>
                <div className="chat-with-staff__options mb-3">
                  {defaultCollegeId && (
                    <label 
                      className={`chat-with-staff__option ${staffTarget === "college" ? "active" : ""}`}
                      style={staffTarget === "college" ? { backgroundColor: "#ffc0cb", borderColor: "#ffc0cb", color: "white" } : {}}
                    >
                      <input
                        type="radio"
                        name="staff-target"
                        value="college"
                        checked={staffTarget === "college"}
                        onChange={() => setStaffTarget("college")}
                      />
                      <FaUniversity className="me-1" />
                      <span>College Staff</span>
                    </label>
                  )}
                  
                  <label 
                    className={`chat-with-staff__option ${staffTarget === "erish" ? "active" : ""}`}
                    style={staffTarget === "erish" ? { backgroundColor: "#ffc0cb", borderColor: "#ffc0cb", color: "white" } : {}}
                  >
                    <input
                      type="radio"
                      name="staff-target"
                      value="erish"
                      checked={staffTarget === "erish"}
                      onChange={() => setStaffTarget("erish")}
                    />
                    <FaUser className="me-1" />
                    <span>Erish Admin</span>
                  </label>
                </div>

                {/* Staff Selection Dropdown */}
                {chatMode === "individual" && (
                  <>
                    <label className="chat-with-staff__label">
                      Select Staff Member
                    </label>
                    
                    {staffLoading ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                        <p className="mt-2 small">Loading staff members...</p>
                      </div>
                    ) : staffList.length > 0 ? (
                      <Dropdown className="w-100 mb-3">
                        <Dropdown.Toggle 
                          variant="outline-secondary" 
                          className="w-100 text-start d-flex justify-content-between align-items-center"
                        >
                          {selectedStaff ? (
                            <>
                              <span>
                                <strong>{selectedStaff.name}</strong>
                                <br />
                                <small className="text-muted">
                                  {selectedStaff.role?.role_name}
                                </small>
                              </span>
                            </>
                          ) : (
                            <span>Select a staff member...</span>
                          )}
                          <FaCaretDown />
                        </Dropdown.Toggle>
                        
                        <Dropdown.Menu className="w-100">
                          {staffList.map((staff) => (
                            <Dropdown.Item 
                              key={staff.user_id}
                              onClick={() => setSelectedStaff(staff)}
                              className="py-2"
                            >
                              <div>
                                <strong>{staff.name}</strong>
                                <div className="d-flex justify-content-between">
                                  <small className="text-muted">{staff.role?.role_name}</small>
                                </div>
                              </div>
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : (
                      <div className="chat-with-staff__hint mb-3">
                        {staffTarget === "college" 
                          ? "No college staff members found."
                          : "No Erish admin members found."}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* College ID Info */}
            {defaultCollegeId && (
              <>
                <label className="chat-with-staff__label">College ID</label>
                <div className="chat-with-staff__field mb-3">
                  {defaultCollegeId} (Auto-detected)
                </div>
              </>
            )}

            {/* File Preview Section */}
            {selectedFile && (
              <div className="mb-3">
                <Alert variant="info" className="d-flex justify-content-between align-items-center">
                  <div>
                    <FaFileUpload className="me-2" />
                    <strong>File attached:</strong> {selectedFile.name}
                    {renderFilePreview()}
                  </div>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={clearSelectedFile}
                  >
                    <FaTimes />
                  </Button>
                </Alert>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <Alert variant="success" className="mb-3">
                {successMessage}
              </Alert>
            )}

            {/* Error Message */}
            {errorMessage && (
              <Alert variant="danger" className="mb-3">
                {errorMessage}
              </Alert>
            )}
          </section>

          {/* Right Panel: Chat View */}
          <section className="chat-with-staff__panel chat-with-staff__panel--thread">
            <div className="chat-shell" style={{ display: 'flex', flexDirection: 'column', height: '600px' }}>
              <div className="chat-shell__header" style={{ flexShrink: 0 }}>
                <div>
                  <div className="chat-shell__title">{getChatTitle()}</div>
                  <div className="chat-shell__subtitle">{getSubtitle()}</div>
                </div>
                <span className={`chat-shell__status chat-shell__status--${socketStatus}`}>
                  {socketStatus}
                </span>
              </div>

              {/* Messages Container */}
              <div
                className="chat-messages p-3"
                style={{ flexGrow: 1, overflowY: "auto", minHeight: 0 }}
              >
                {historyLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-2">Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <FaComments size={48} className="mb-3" />
                    <h5>No messages yet</h5>
                    <p>
                      {chatMode === "individual" 
                        ? "Select a staff member and start chatting" 
                        : "Send a message to start group conversation"}
                    </p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.chat_id || `${msg.sender_id}-${msg.sent_at}`}
                      className={`chat-bubble mb-3 p-3 rounded ${
                        msg.sender_id === Number(senderId)
                          ? "text-white ms-auto"
                          : "bg-light me-auto"
                      }`}
                      style={{ 
                        maxWidth: "70%",
                        backgroundColor: msg.sender_id === Number(senderId) ? "#ffc0cb" : undefined,
                        color: msg.sender_id === Number(senderId) ? "white" : undefined,
                        textAlign: "left"
                      }}
                    >
                      <p className="mb-0">{renderMessageTextWithLinks(msg.message, msg.sender_id === Number(senderId))}</p>
                      
                      {/* File Attachment Display - AdminChat.js style */}
                      {msg.url && (
                        <div className="mt-2">
                          {msg.url.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                            <>
                              <video 
                                controls 
                                className="w-100 rounded" 
                                style={{ maxHeight: '200px' }}
                              >
                                <source src={msg.url} type="video/mp4" />
                              </video>
                              {msg.file_name && <div className="small text-muted mt-1">{msg.file_name}</div>}
                            </>
                          ) : msg.url.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i) ? (
                            <>
                              <img 
                                src={msg.url} 
                                alt="Attachment" 
                                className="w-100 rounded" 
                                style={{ maxHeight: '200px', objectFit: 'cover' }} 
                              />
                              {msg.file_name && <div className="small text-muted mt-1">{msg.file_name}</div>}
                            </>
                          ) : (
                            <a 
                              href={msg.url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-decoration-none"
                            >
                              <div className="border rounded p-2 bg-white mt-2">
                                <FaFileUpload className="me-2" />
                                <strong>Document:</strong> {msg.file_name || 'Download file'}
                              </div>
                            </a>
                          )}
                        </div>
                      )}

                      <div 
                        className="small mt-2" 
                        style={{ 
                          fontSize: "11px", 
                          color: msg.sender_id === Number(senderId) ? "rgba(255, 255, 255, 0.75)" : "#787878",
                          textAlign: "left",
                          fontWeight: "500"
                        }}
                      >
                        {formatMsgTime(msg.sent_at)} {getSenderLabel(msg)}
                      </div>
                    </div>
                  ))
                )}
                <div ref={scrollRef} />
              </div>

              {/* Message Input Area */}
              <div className="chat-shell__composer mt-3" style={{ flexShrink: 0 }}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Type your message here..."
                    disabled={isSending}
                  />
                  
                  {/* Hidden file input - AdminChat.js style */}
                  <Form.Control
                    type="file"
                    ref={fileInputRef}
                    hidden
                    accept="image/*,video/*,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileSelect}
                  />
                  
                  <Button
                    variant="outline-secondary"
                    onClick={triggerFileInput}
                    disabled={isSending}
                    title="Attach file"
                  >
                    <FaPaperclip />
                  </Button>
                  
                  <Button
                    variant="primary"
                    onClick={handleSend}
                    disabled={
                      (chatMode === "individual" && !selectedStaff) || 
                      (!draft.trim() && !selectedFile) ||
                      isSending ||
                      socketStatus !== "connected"
                    }
                  >
                    {isSending ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Sending...
                      </>
                    ) : (
                      'Send'
                    )}
                  </Button>
                </InputGroup>
                
                <div className="mt-2 text-muted small">
                  Press Enter to send, Shift+Enter for new line
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ChatWithStaff;
